import { PropsWithChildren } from "react";
import { clsx } from "clsx";
import { CircleAlert, TriangleAlert, CircleCheckBig, X } from "lucide-react";

import { twMerge } from "@/design-system/twMerge";

type BannerKind = "alert" | "note";
type AlertType = "error" | "warning" | "success";
type NoteType = "info" | "warning";
type BannerType = AlertType | NoteType;

type BannerProps = PropsWithChildren<{
  kind?: BannerKind;
  type: BannerType;
  message?: string;
  onClose?: () => void;
  className?: string;
}>;

const alertOptions = {
  error: {
    color: "text-status-error",
    backgroundColor: "bg-status-error-tint",
    borderColor: "border-status-error",
    Icon: CircleAlert,
  },
  warning: {
    color: "text-status-warning",
    backgroundColor: "bg-status-warning-tint",
    borderColor: "border-status-warning",
    Icon: TriangleAlert,
  },
  success: {
    color: "text-status-success",
    backgroundColor: "bg-status-success-tint",
    borderColor: "border-status-success",
    Icon: CircleCheckBig,
  },
} as const;

const noteOptions = {
  info: {
    // No status-info-adjacent border token exists at this subtler weight
    // yet (border-status-info is bolder, meant for the alert accent bar);
    // left as a primitive rather than forcing a visual change here.
    borderColor: "border-blue-200",
    backgroundColor: "bg-status-info-tint",
    hasIcon: false,
  },
  warning: {
    borderColor: "border-yellow-200",
    backgroundColor: "bg-status-warning-tint",
    iconColor: "text-status-warning",
    hasIcon: true,
  },
} as const;

export function Banner({
  kind = "alert",
  type,
  children,
  message,
  onClose,
  className,
}: BannerProps) {
  if (kind === "note") {
    const noteType: NoteType = type === "warning" ? "warning" : "info";
    const options = noteOptions[noteType];

    return (
      <div
        className={twMerge(
          clsx(
            "text-body-sm relative rounded-lg border p-4",
            noteType === "warning" && "flex gap-4",
            options.borderColor,
            options.backgroundColor,
            className,
          ),
        )}
      >
        {options.hasIcon && (
          <span>
            <TriangleAlert className={options.iconColor} />
          </span>
        )}
        <div className="flex flex-grow flex-col">
          {message && <span className="text-heading-sm">{message}</span>}
          <div className={clsx(message && children && "mt-1")}>{children}</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="hover:bg-opacity-10 text-textDefault focus:outline-interactive-selected absolute top-2 right-2 rounded-full p-1 hover:cursor-pointer focus:outline-2"
            aria-label="Close banner"
          >
            <X />
          </button>
        )}
      </div>
    );
  }

  const alertType: AlertType =
    type === "error" || type === "warning" || type === "success"
      ? type
      : "warning";
  const options = alertOptions[alertType];

  return (
    <div
      className={twMerge(
        clsx(
          "bg-surface-default relative flex items-center gap-4 rounded border-l-4 p-4 shadow-md",
          options.borderColor,
          className,
        ),
      )}
    >
      <span
        className={clsx(
          "flex items-center self-start rounded-full p-2",
          options.backgroundColor,
        )}
      >
        <options.Icon className={options.color} />
      </span>
      <div className="flex flex-grow flex-col">
        {message && <span className="text-heading-sm">{message}</span>}
        <div className={clsx(message && children && "mt-1")}>{children}</div>
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="hover:bg-opacity-10 text-textDefault focus:outline-interactive-selected absolute top-2 right-2 rounded-full p-1 hover:cursor-pointer focus:outline-2"
          aria-label="Close banner"
        >
          <X />
        </button>
      )}
    </div>
  );
}
